import React, { SyntheticEvent, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Tab, Grid, Header, Card, Image, TabProps } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { useStore } from "../../app/stores/store";
import { UserGame } from '../../app/models/Profile';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

export default observer(function ProfileActivities() {
    const { profileStore } = useStore();
    const {
        loadUserGames,
        profile,
        loadingGames,
        userGames
    } = profileStore;
    const { t } = useTranslation();
    const [isMobile, setIsMobile] = useState(false);

    // Check if device is mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const getLocaleFromLanguage = (language: string) => {
        const localeMap: { [key: string]: string } = {
          'en': 'en-US',
          'zh': 'zh-CN',
          'es': 'es-ES',
          'hi': 'hi-IN',
          'ar': 'ar-SA',
          'pt': 'pt-PT',
          'ru': 'ru-RU',
          'bn': 'bn-BD',
          'fr': 'fr-FR',
          'de': 'de-DE',
          'ja': 'ja-JP',
          'ur': 'ur-PK',
          'ro': 'ro-RO',
          'it': 'it-IT',
          'hu': 'hu-HU',
          'el': 'el-GR',
          'tr': 'tr-TR',
          'sr': 'sr-RS',
          'srLatn': 'sr-Latn-RS'
        };
        return localeMap[language] || language;
      };
      
      const formatDateDisplay = (date: Date | string) => {
        const locale = getLocaleFromLanguage(i18n.language);
        // Ensure we have a Date object
        const dateObj = date instanceof Date ? date : new Date(date);
        
        return dateObj.toLocaleDateString(locale, {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      };
      
    
    const [activeIndex, setActiveIndex] = useState(0);

    // Move panes definition inside component to access t function
    const panes = [
        { menuItem: t('profileActivities.gamesFollowing'), pane: { key: 'following' } },
        { menuItem: t('profileActivities.futureGames'), pane: { key: 'future' } },
        { menuItem: t('profileActivities.pastGames'), pane: { key: 'past' } },
        { menuItem: t('profileActivities.hosting'), pane: { key: 'host' } },
    ];

    useEffect(() => {
        if (profile) {
            // Use the key of the active tab's pane
            const predicate = panes[activeIndex].pane.key;
            loadUserGames(profile.userID, predicate);
        }
    }, [loadUserGames, profile, activeIndex]);

    const handleTabChange = (e: SyntheticEvent, data: TabProps) => {
        setActiveIndex(data.activeIndex as number);
    };

    return (
        <Tab.Pane loading={loadingGames}>
            <Grid>
                <Grid.Column width={16}>
                    <Header floated='left' icon='calendar' content={t('profileActivities.games')} />
                </Grid.Column>
                <Grid.Column width={16}>
                    <Tab
                        panes={panes}
                        activeIndex={activeIndex}
                        menu={{ 
                            secondary: true, 
                            pointing: true,
                            stackable: true
                        }}
                        onTabChange={handleTabChange}
                    />
                    <br />
                    <Card.Group itemsPerRow={isMobile ? 1 : 4}>
                        {userGames.map((game: UserGame) => (
                            <Card
                                as={Link}
                                to={`/games/${game.id}`}
                                key={game.id}
                                style={{
                                    maxWidth: '100%',
                                    wordWrap: 'break-word',
                                    overflow: 'hidden'
                                }}>
                                <Image
                                    src={`/assets/categoryImages/${game.sportName}.jpg`}
                                    style={{
                                        minHeight: 100, objectFit: 'cover'
                                    }}
                                />
                                <Card.Content>
                                    <Card.Header 
                                        textAlign='center'
                                        style={{
                                            fontSize: 'clamp(0.9rem, 2.5vw, 1.28rem)',
                                            lineHeight: '1.3',
                                            wordBreak: 'break-word',
                                            overflowWrap: 'break-word',
                                            hyphens: 'auto',
                                            padding: isMobile ? '0.5rem 0.25rem' : undefined
                                        }}
                                    >
                                        {game.gameName}
                                    </Card.Header>
                                    <Card.Meta 
                                        textAlign='center'
                                        style={{
                                            fontSize: 'clamp(0.7rem, 2vw, 0.9rem)',
                                            lineHeight: '1.2',
                                            wordBreak: 'break-word',
                                            padding: isMobile ? '0.25rem' : undefined
                                        }}
                                    >
                                        <div style={{ marginBottom: '0.25rem' }}>
                                            {formatDateDisplay(game.dateTimeStart!)}
                                        </div>
                                        <div>
                                            {format(new Date(game.dateTimeStart), 'h:mm a')}
                                        </div>
                                    </Card.Meta>
                                </Card.Content>
                            </Card>
                        ))}
                    </Card.Group>
                </Grid.Column>
            </Grid>
        </Tab.Pane>
    );
});